LicenseInfo.setLicenseKey(
  "aa506eee1707a2d35f55fc499f89e0d8Tz00ODc0MSxFPTE2OTE1OTc2ODA5ODIsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=",
)

import React, { useEffect, useMemo, useRef, useState } from "react"

import { Link, Paper } from "@mui/material"
import { styled } from "@mui/styles"
import {
  GridRow,
  DataGridPro as TableComponent,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-pro"
import { LicenseInfo } from "@mui/x-license-pro"
import { debounce, isEmpty } from "lodash"
import { any, isNil, length, propEq } from "ramda"
import { cx } from "ramda-extension"

import { useStyles } from "./css"
import { CustomizationDialog } from "./customization"
import { FilterButton } from "./filter-button"
import {
  disableColumnMenu,
  getLocalStorageValues,
  getTreeDataPath,
  mapDataToRows,
  mapTableInfo,
  setColumnProperties,
  setLocalStorageValues,
} from "./lib"
import { Status } from "./status-box"
import { Drawer } from ".."
import index18n from "../../../lib/i18n.es6"
import { ButtonContainer, MuiIcon, TextField, Toolbar, Tooltip } from "../../atoms"
import { IconButton } from "../../molecules"
import { LinearProgress } from "../../mui-reexports"


const FIRST_COLUMN_WIDTH = 1
const DEFAULT_COLUMN_WIDTH = 0.3

const NoRowsOverlay = ({ additionalClassName, noResultsText }) => {
  return <div className={additionalClassName}>{noResultsText}</div>
}

const CustomRow = React.memo((props) => {
  const {
    disallowSelectAll,
    index,
    lastColumnToRender,
    notification,
    onRowClick,
    sectionItem,
    selectAllHandler,
    selectAllToPage,
    selectHints,
    selected,
    selectedRowKeys,
    singleSelection,
    ...anotherProps
  } = props
  const apiReference_ = useGridApiContext()

  const SelectionHint = () =>
    useMemo(
      () => (
        <>
          {!index && selectHints && (
            <div className={notification}>
              <span>{selectHints.text}&nbsp;</span>
              <Link
                component="button"
                onClick={selectAllHandler}
                underline="hover"
                variant="inherit"
                color="#0055b0">
                {selectHints.link}
              </Link>
            </div>
          )}
        </>
      ),
      [index, selectHints],
    )

  const rowNode = apiReference_.current.getRowNode(props.rowId)
  const isChildNode = rowNode && rowNode.depth > 0

  if (isChildNode) return null

  return (
    <React.Fragment key={`${props.rowId}Fragment`}>
      {!selectAllToPage && !disallowSelectAll && !singleSelection && <SelectionHint />}

      <GridRow
        onClick={onRowClick}
        {...anotherProps}
        index={index}
        key={props.rowId}
        selected={selected}
        lastColumnToRender={lastColumnToRender}
        renderedColumns={props.renderedColumns}
        className={`${isChildNode && sectionItem} ${rowNode.id === "notification" && notification}`}
      />

      {props?.row?.questions?.map((item, childKey) => {
        const childProps = {
          ...anotherProps,
          index,
          lastColumnToRender,
          row: { ...item },
          rowId: item.id,
          selected: false,
        }

        return (
          <GridRow
            className={sectionItem}
            key={childKey}
            {...childProps}
            renderedColumns={props.renderedColumns}
          />
        )
      })}
    </React.Fragment>
  )
})

const iconColumn = (icon, setContentInfo, handleDrawerOpen, drawerData) => {
  return {
    dataIndex: "icon",
    default: true,
    disableReorder: true,
    disableColumnMenu: true,
    field: "icon",
    flex: 0,
    headerName: "",
    isLocked: true,
    isSettings: true,
    minWidth: 30,
    headerClassName: "Cirrus-drawerIconColumn",
    cellClassName: "Cirrus-drawerIconColumn",
    renderCell: (params) => {
      return (
        <IconButton
          muiName="InfoOutlined"
          onClick={(event) => {
            event.stopPropagation()
            setContentInfo({
              details: mapTableInfo(drawerData?.details, params.row),
              overview: mapTableInfo(drawerData?.overview, params.row),
              title: params.row[drawerData?.titleProperty],
            })
            handleDrawerOpen(true)
          }}
        />
      )
    },
    sortable: false,
    width: 20,
  }
}

const customizeColumn = (actionColumn) => ({
  align: "right",
  dataIndex: "customizeColumn",
  default: true,
  disableReorder: true,
  field: "custom",
  flex: 0,
  headerName: "",
  isLocked: true,
  isSettings: true,
  minWidth: 50,
  sortable: false,
  width: 50,
  ...(actionColumn || {}),
})

export const Table = ({
  alert,
  singleSelection = false,
  actionColumn,
  columnsData,
  customIcon,
  data = [],
  disallowSelectAll = false,
  drawerData = { title: "", overview: {}, details: {} },
  disableSelectionOnClick = true,
  enableInfoIcon = true,
  enableFirstColumnWidth = true,
  filterPosition,
  renderFilters,
  getRowClassName = () => "",
  handleSort,
  handleChangePage,
  handleChangeRowsPerPage,
  hideCustomizeColumn = false,
  noResultsText = index18n("General:NoResults"),
  onStateChange = (event) => event,
  onRowSelected,
  page = 0,
  rowHandler,
  rowsPerPage = 100,
  total = 0,
  disableSearch = false,
  selectAll = index18n("Framework.TableSelectionHint:SelectAllInTable").replace("{0}", total),
  selectedAllHandler = () => false,
  selectedRows = [],
  selectPage = index18n("General:SelectAll").replace("{0}", length(data)),
  selectPageHint = index18n("General:SelectPageHint").replace("{0}", length(data)),
  selectTableHint = index18n("General:SelectTableHint").replace("{0}", total),
  autoHeight = true,
  tableHeight,
  tableLegend,
  toolbarButtonLeft,
  toolbarButtonRight,
  withCheckbox = true,
  loading,
  setTableApi = () => {},
  tableId,
  statusType = "default",
  wrapInline = false,
  rowHeight = 52,
  onTableChange,
  autoLoad = false,
  appearance = {},
  searchLabel,
  selectAllToPage = false,
  searchOnLeft = false,
  noHover = false,
  onRowsScrollEnd,
}) => {
  const firstColumnIndex =
    (enableInfoIcon && customIcon && 3) ||
    ((enableInfoIcon || customIcon) && 2) ||
    (!withCheckbox && !enableInfoIcon && "0") ||
    1

  const memoizedActionColumn = useMemo(() => actionColumn, [actionColumn])

  const additionalStyles = {
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "transparent",
      cursor: "default",
    },
  }

  const {
    activeColumn,
    checkbox,
    columnHeader,
    columnSeparator,
    first,
    header,
    hiddenColumn,
    icon,
    name,
    noResults,
    notification,
    paper,
    root,
    row,
    sectionHeader,
    sectionItem,
    sortableColumn,
    title,
  } = useStyles({
    disallowSelectAll: disallowSelectAll || singleSelection,
    firstColumnIndex,
    selectAllToPage,
    withCheckbox,
    ...appearance,
  })

  //move into the app wrapper
  const apiReference = useGridApiRef()
  const localStorageSort = getLocalStorageValues(tableId, "sort")

  const [open, setOpen] = useState(false)
  const containerReference = useRef(null)
  const [contentInfo, setContentInfo] = useState({ title: "", overview: {}, details: {} })
  const [selectAllInfo, setSelectAllInfo] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [tablePage, setTablePage] = useState(page)
  const [tableRowPerPage, setRowPerPage] = useState(rowsPerPage)
  const [resetFilter, setResetFilter] = useState(() => null)
  const [reload, setReload] = useState(false)
  const [showHeaderTooltip, setHeaderTooltip] = useState(false)
  const [defaultSort, setDefaultSort] = useState(
    localStorageSort || defaultSort ? [localStorageSort || defaultSort] : [],
  )
  const [filterState, setFilterState] = useState({})
  const [tableSearch, setTableSearch] = useState("")
  const [tableState, setTableState] = useState({
    customizationSettings: [],
    dataColumns: [],
    defaultColumns: [],
    sortModel: [],
    visibleColumns: false,
    withSections: null,
  })

  useEffect(() => {
    apiReference?.current?.setPage?.(0)
  }, [tableId])

  useEffect(() => {
    try {
      if (isEmpty(apiReference?.current)) return

      apiReference.current.scroll({ top: 0 })
      apiReference.current.setPage(0)
    } catch (error) {
      // "apiReference.current" throws an error: "scroll is not a function" though it gets executed
    }
  }, [apiReference?.current, tableSearch, defaultSort, filterState, tableId])

  useEffect(() => {
    if (singleSelection) {
      const idsKeys = selectedRowKeys

      if (idsKeys.length > 1) {
        const [firstItem] = idsKeys
        const lastItem = idsKeys.pop()

        if (lastItem && selectedRowKeys.length && firstItem !== lastItem) {
          apiReference.current.selectRow(lastItem, true, true)
        }
      }
    }
  }, [selectedRowKeys])

  const search = useMemo(
    () => (!disableSearch ? debounce(setTableSearch, 300) : null),
    [disableSearch],
  )

  useEffect(() => {
    return () => search && search.cancel()
  }, [])

  const apiHandlers = {
    reload: () => setReload(!reload),
    resetFilters: () => {
      if (resetFilter) {
        resetFilter?.()
      } else {
        setFilterState({})
      }
    },
    resetSearch: () => {
      const searchElement = document.querySelector(`#searchInput${tableId}`)

      setTableSearch("")
      if (searchElement) searchElement.value = ""
    },
    resetSelections: () => {
      setSelectedRowKeys([])
      apiReference?.current?.setSelectionModel?.([])
    },
    setSearch: search,
    setSelectedRowIds: (ids) => {
      setSelectedRowKeys(ids)
      apiReference?.current?.setSelectionModel?.(ids)
    },
    setSort: (sortModel = null, customTableId = null) => {
      if (sortModel && sortModel.length) {
        apiReference.current?.setSortModel(sortModel)
        setLocalStorageValues(customTableId || tableId, "sort", sortModel[0])
      }
    },
  }

  useEffect(() => {
    const updateData = {
      filterState,
      filters: getLocalStorageValues(tableId, "filters") || filterState.values || {},
      page: tablePage,
      pageSize: tableRowPerPage,
      search: tableSearch,
      sort: Array.isArray(defaultSort) ? defaultSort[0] : defaultSort,
    }

    setTableApi({
      api: apiReference,
      ...apiHandlers,
      state: {
        ...updateData,
      },
      selectedIds: selectedRowKeys,
    })
    if (updateData.sort?.field || autoLoad) onTableChange?.(updateData)

    apiHandlers.resetSelections()
  }, [defaultSort, filterState, tableSearch, tablePage, tableRowPerPage, reload, tableId])

  useEffect(() => {
    setTableApi({
      api: apiReference,
      ...apiHandlers,
      selectedIds: selectedRowKeys,
      state: {},
    })
  }, [])

  useEffect(() => {
    const localStorageSort = getLocalStorageValues(tableId, "sort")

    setTableState({
      ...tableState,
      sortModel: localStorageSort || defaultSort ? [localStorageSort || defaultSort] : [],
    })
  }, [])

  const groupingColDef = useMemo(
    () => ({
      cellClassName: hiddenColumn,
      disableColumnMenu: true,
      headerClassName: hiddenColumn,
      headerName: "",
      sortable: false,
      width: 1,
    }),
    [],
  )

  const selectAllHandler = () => {
    if (total < pageSize) {
      setSelectAllInfo({
        isSelectedAll: "page",
        selectHints: {
          link: "",
          text: selectPageHint,
        },
      })
      selectedAllHandler(false)
    } else {
      const isSelectedAll = selectAllInfo.isSelectedAll === "all" ? "page" : "all"

      setSelectAllInfo({
        isSelectedAll,
        selectHints: {
          link: isSelectedAll === "all" ? selectPage : selectAll,
          text: isSelectedAll === "all" ? selectTableHint : selectPageHint,
        },
      })
      selectedAllHandler(isSelectedAll === "all")
    }
  }

  useEffect(() => {
    const isAllSelected = selectedRowKeys.length === data.length
    const selectValues = {}

    selectValues.selectHints = total > pageSize && isAllSelected ? {
        link: selectAllInfo.isSelectedAll === "all" ? selectPage : selectAll,
        text: selectAllInfo.isSelectedAll === "all" ? selectTableHint : selectPageHint,
    } : false;

    selectValues.isSelectedAll = isAllSelected ? "page" : false

    if (selectValues?.selectHints?.text !== selectAllInfo?.selectHints?.text)
      setSelectAllInfo(selectValues)

    selectedAllHandler(false)

    const updateData = {
      filterState,
      filters: getLocalStorageValues(tableId, "filters") || filterState.values || {},
      page: tablePage,
      pageSize: tableRowPerPage,
      search: tableSearch,
      sort: Array.isArray(defaultSort) ? defaultSort[0] : defaultSort,
    }

    setTableApi({
      state: {
        ...updateData,
      },
      api: apiReference,
      ...apiHandlers,
      selectedIds: selectedRowKeys,
    })

    if (onRowSelected) onRowSelected(selectedRowKeys)
  }, [selectedRowKeys])

  const onColumnOrderChangeHandler = ({ field, targetIndex }) => {
    const { result, updatedColumn } = tableState.dataColumns.reduce(
      (accum, item) => {
        if (item.isSettings) return accum

        item.field === field ? (accum.updatedColumn = item) : accum.result.push(item)

        return accum
      },
      { result: [], updatedColumn: {} },
    )

    result.splice(targetIndex - 2, 0, updatedColumn)

    const { columns, order } = result
      .sort((a, b) => (a.isFirstDefault ? -1 : b.isFirstDefault ? 1 : 0))
      .reduce(
        (accum, item, index) => {
          const notFirstColumnSize =
            item.flex === FIRST_COLUMN_WIDTH ? DEFAULT_COLUMN_WIDTH : item.flex

          accum.columns.push({
            ...item,
            flex: index === 0 && enableFirstColumnWidth ? FIRST_COLUMN_WIDTH : notFirstColumnSize,
            index,
          })
          accum.order[item.field] = index

          return accum
        },
        { columns: [], order: {} },
      )

    setLocalStorageValues(tableId, "order", order)

    const dataColumns = [
      ...(enableInfoIcon ? [iconColumn(icon, setContentInfo, setOpen, drawerData)] : []),
      ...(customIcon ? [customIcon] : []),
      ...columns,
      ...(hideCustomizeColumn ? [] : [customizeColumn(memoizedActionColumn)]),
    ]

    disableColumnMenu(dataColumns, tableId)

    setTableState({
      ...tableState,
      dataColumns,
    })
  }

  useEffect(() => {
    const withSections = any(propEq("isSection", true))(data)

    setTableState({
      ...tableState,
      withSections,
    })
  }, [data])

  const pageSize = useMemo(() => {
    const rowsPerPageLocal = getLocalStorageValues(tableId, "rowsPerPage")

    if (!rowsPerPageLocal) {
      setLocalStorageValues(tableId, "rowsPerPage", rowsPerPage)
    }

    if (rowsPerPageLocal && +rowsPerPageLocal !== rowsPerPage) {
      setRowPerPage(+rowsPerPageLocal)
      // TODO remove if not need
      handleChangeRowsPerPage?.(+rowsPerPageLocal)
    }

    return rowsPerPageLocal || tableRowPerPage
  }, [rowsPerPage, tableRowPerPage])

  const onPageSizeChangeHandler = (value) => {
    setLocalStorageValues(tableId, "rowsPerPage", value)
    handleChangeRowsPerPage?.(value)
  }

  useEffect(() => {
    const localStorageSort = getLocalStorageValues(tableId, "sort")
    const sortModel = localStorageSort || columnsData?.filter((item) => item.defaultSort)[0]
    const combinedColumns = [
      ...(enableInfoIcon ? [iconColumn(icon, setContentInfo, setOpen, drawerData)] : []),
      ...(customIcon ? [customIcon] : []),
      ...setColumnProperties(columnsData, autoLoad),
      ...(hideCustomizeColumn ? [] : [customizeColumn(memoizedActionColumn)]),
    ]

    disableColumnMenu(combinedColumns)
    const defaultVisibleColumns = getLocalStorageValues(tableId, "columns")
    const defaultOrder = columnsData.reduce((order, item, index) => {
      order[item.field] = index

      return order
    }, {})
    const storageOrder = getLocalStorageValues(tableId, "order")
    const order = storageOrder || defaultOrder

    if (!storageOrder) setLocalStorageValues(tableId, "order", order)

    const { customizationSettings, visibleColumns } = combinedColumns.reduce(
      (columns, item, index) => {
        if (item.hidden) return columns

        if (isNil(columns.visibleColumns[item.field])) {
          columns.visibleColumns[item.field] = item.default
        }

        columns.customizationSettings.push(item.default ? { ...item, isVisible: true } : item)

        const lastIndex = columns.customizationSettings.length - 1

        columns.customizationSettings[lastIndex].index = lastIndex

        return columns
      },
      { customizationSettings: [], visibleColumns: { ...defaultVisibleColumns } },
    )

    const dataColumns = customizationSettings
      .map((item, index) => {
        const { field, headerName, isSorterActive, sortable } = item
        const itemIndex = isNil(order[field]) ? index : order[field]
        const notFirstColumnSize =
          item.flex === FIRST_COLUMN_WIDTH ? DEFAULT_COLUMN_WIDTH : item.flex

        if (!item.isSettings) {
          item.flex =
            itemIndex === 0 && enableFirstColumnWidth ? FIRST_COLUMN_WIDTH : notFirstColumnSize
        }

        const column = {
          ...item,
          headerClassName: cx(
            sortable && sortableColumn,
            isSorterActive && activeColumn,
            index === 0 && first,
          ),
          index:
            item.field === (customIcon || {}).field
              ? 0
              : isNil(order[field])
              ? index
              : order[field],
          renderHeader: () => {
            if (item.tooltip || showHeaderTooltip) {
              const tooltipText = `${headerName || field}${item.tooltip ? `. ${item.tooltip}` : ""}`

              return (
                <Tooltip title={tooltipText} placement={"bottom"}>
                  {headerName || field}
                </Tooltip>
              )
            }

            if (item.isPreview) return <div></div>

            return <div>{headerName || field}</div>
          },
        }

        if (item.isPreview) {
          column.renderCell = (props) => {
            const { value } = props

            if (value) return item.isPreview?.(value)
          }
        }

        if (
          (column.field === "status" || column.field === "passedStatus") &&
          !column.customStatus
        ) {
          column.renderCell = (props) => {
            const {
              row: { passedStatus, status, statusColor },
            } = props

            return (
              <Status statusType={statusType} color={statusColor} status={status || passedStatus} />
            )
          }
        } else if (column.field === "question") {
          column.renderCell = (props) => {
            const {
              row: { isSection, question },
            } = props

            return <div className={cx(isSection && sectionHeader, title)}>{question}</div>
          }
        } else if (column.field === "icon" || column.field === "custom") {
          column.renderHeader = () => <div></div>
        }

        return column
      })
      .sort((a, b) => a.index - b.index)

    if (enableFirstColumnWidth) {
      dataColumns[firstColumnIndex - 1].flex = FIRST_COLUMN_WIDTH
    }

    if (!getLocalStorageValues(tableId, "visibleColumns")) {
      setLocalStorageValues(tableId, "visibleColumns", visibleColumns)
    }

    if (sortModel)
      setDefaultSort({
        field: sortModel.field,
        sort: sortModel.defaultSort || sortModel.sort,
      })

    setTableState({
      ...tableState,
      customizationSettings,
      dataColumns,
      defaultColumns: dataColumns,
      defaultOrder,
      visibleColumns,
    })
    // TODO for Maksim endless loading with memoizedActionColumn
    // temp fix for https://eassessment.atlassian.net/browse/CR-20673
  }, [columnsData, showHeaderTooltip /*, memoizedActionColumn*/])

  useEffect(() => {
    const visibleColumns = getLocalStorageValues(tableId, "visibleColumns")

    if (visibleColumns) {
      Object.keys(visibleColumns).forEach((item) => {
        apiReference?.current?.setColumnVisibility?.(item, visibleColumns[item])
      })
    }

    // TODO remove this if case after all tests
    if (Array.isArray(getLocalStorageValues(tableId, "columns"))) {
      setLocalStorageValues(tableId, "columns", {})
      return
    }

    setHeaderTooltip(Object.values(visibleColumns || {})?.filter((item) => item)?.length >= 10)

    if (Object.values(tableState.visibleColumns).length) {
      setLocalStorageValues(tableId, "columns", tableState.visibleColumns)
    }
  }, [tableState])

  const onRowClick = (data, event, details) => {
    if (
      !event ||
      !event.target ||
      event.target.tagName === "INPUT" ||
      event.target.type === "checkbox"
    )
      return

    if (rowHandler) rowHandler(data, event)
  }

  const rowsData = useMemo(() => (tableState.withSections ? mapDataToRows(data) : data), [data])

  return (
    <Container classes={{ root: paper }} elevation={1} /*style={{ height: tableHeight }}*/>
      {useMemo(
        () => (
          <>
            {open && (
              <div
                style={{ bottom: 0, left: 0, position: "fixed", right: 0, top: 0, zIndex: 1 }}
                onClick={() => setOpen(false)}
              />
            )}
            <Drawer
              open={open}
              onClose={() => setOpen(false)}
              anchor="right"
              width={390}
              height="100%"
              title={contentInfo && contentInfo.title}
              container={containerReference}
              tableInfo={contentInfo}
            />
          </>
        ),
        [open],
      )}
      {alert}
      {useMemo(() => {
        if (disableSearch && !toolbarButtonLeft && !toolbarButtonRight && !renderFilters)
          return null

        return (
          <Toolbar
            {...{
              contentEnd: (
                <ButtonContainer>
                  {toolbarButtonRight}
                  {renderFilters && (
                    <FilterButton
                      setResetFilter={setResetFilter}
                      render={renderFilters}
                      tableId={tableId}
                      setFilterState={setFilterState}
                      position={
                        filterPosition || {
                          anchorOrigin: {
                            horizontal: "right",
                            vertical: "top",
                          },
                          transformOrigin: {
                            horizontal: "right",
                            vertical: "top",
                          },
                        }
                      }
                    />
                  )}
                  {!disableSearch && !searchOnLeft && (
                    <TextField
                      id={`searchInput${tableId}`}
                      size="medium"
                      className="searchField"
                      disableUnderline={true}
                      placeholder={searchLabel || index18n("General:Search")}
                      startAdornment={<MuiIcon.Search />}
                      onChange={(event) => {
                        search(event.target.value)
                      }}
                    />
                  )}
                </ButtonContainer>
              ),

              contentStart: (
                <>
                  {!disableSearch && searchOnLeft && (
                    <TextField
                      id={`searchInput${tableId}`}
                      size="medium"
                      className="searchField"
                      disableUnderline={true}
                      placeholder={searchLabel || index18n("General:Search")}
                      startAdornment={<MuiIcon.Search />}
                      onChange={(event) => {
                        search(event.target.value)
                      }}
                    />
                  )}
                  {toolbarButtonLeft}
                </>
              ),
            }}
          />
        )
      }, [renderFilters, toolbarButtonLeft, toolbarButtonRight, tableId, disableSearch])}
      {loading && <LinearProgress />}
      {useMemo(
        () =>
          tableState.visibleColumns && (
            <TableComponent
              sx={noHover ? additionalStyles : {}}
              rowHeight={rowHeight}
              checkboxSelection={withCheckbox}
              disableSelectionOnClick={disableSelectionOnClick}
              columns={tableState.dataColumns}
              rows={rowsData}
              autoHeight={autoHeight}
              disableColumnResize={true}
              pagination
              pageSize={pageSize}
              // className={root}
              getRowClassName={getRowClassName}
              onSortModelChange={(model) => {
                if (model.length) {
                  setDefaultSort(model[0])
                  setLocalStorageValues(tableId, "sort", model[0])
                  handleSort?.(model[0])
                }
              }}
              sortingOrder={["asc", "desc"]}
              onPageChange={(value) => {
                setTablePage(value)
                handleChangePage?.(value)
              }}
              rowsPerPageOptions={[10, 30, 50, 100]}
              onPageSizeChange={(value) => {
                setRowPerPage(value)
                onPageSizeChangeHandler?.(value)
              }}
              onRowsScrollEnd={onRowsScrollEnd}
              rowBuffer={1000}
              rowTreshhold={1000}
              paginationMode={"server"}
              sortingMode={"server"}
              rowCount={total}
              scrollEndThreshold={0}
              sortModel={localStorageSort || defaultSort ? [localStorageSort || defaultSort] : []}
              onStateChange={onStateChange}
              onColumnOrderChange={onColumnOrderChangeHandler}
              initialState={{
                columns: {
                  columnVisibilityModel: getLocalStorageValues(tableId, "visibleColumns"),
                },
                selection: selectedRows,
              }}
              isRowSelectable={(params) =>
                tableState.withSections ? length(params.row.sectionPath) === 1 : true
              }
              classes={{
                checkboxInput: checkbox,
                columnHeader,
                columnHeaders: header,
                columnSeparator,
                row,
              }}
              onSelectionModelChange={(newSelectionModel) => {
                if (!loading) setSelectedRowKeys(newSelectionModel)
              }}
              apiRef={apiReference}
              getTreeDataPath={getTreeDataPath}
              defaultGroupingExpansionDepth={0}
              onRowClick={(...args) => {
                if (!loading) onRowClick(...args)
              }}
              components={{
                ColumnMenu: () => (
                  <CustomizationDialog
                    {...{
                      setHeaderTooltip,
                      setTableState,
                      tableId,
                      tableState,
                    }}
                  />
                ),
                ColumnSortedAscendingIcon: () => <MuiIcon.ArrowDropUp />,
                ColumnSortedDescendingIcon: () => <MuiIcon.ArrowDropDown />,
                NoRowsOverlay: () => (
                  <NoRowsOverlay {...{ additionalClassName: noResults, noResultsText }} />
                ),
                Row: (props) => (
                  <CustomRow
                    {...{
                      ...props,
                      disallowSelectAll,
                      notification,
                      onRowClick,
                      sectionItem,
                      selectAllHandler,
                      selectAllToPage,
                      selectHints: selectAllInfo.selectHints,
                      selected: selectedRowKeys.includes(props.rowId) || props.selected,
                      selectedRowKeys,
                      singleSelection,
                    }}
                  />
                ),
              }}
              componentsProps={{
                pagination: {
                  labelDisplayedRows: ({ count, from, to }) =>
                    `${from} - ${to} ` + index18n("General:Of") + ` ${count}`,
                  labelRowsPerPage: index18n("General:RowsPerPage"),
                },
              }}
              localeText={{
                footerRowSelected: (count) =>
                  `${count} ` +
                  (count === 1
                    ? index18n("General:RowSelected")
                    : index18n("General:RowsSelected")),
              }}
              treeData={tableState.withSections}
              groupingColDef={groupingColDef}
            />
          ),
        [rowsData, tableState, selectAllInfo, selectedRowKeys, tableState.dataColumns],
      )}
      {useMemo(
        () => (
          <input
            type="hidden"
            id={`${tableId}_input`}
            value={
              selectAllInfo.isSelectedAll === "all"
                ? selectAllInfo.isSelectedAll
                : selectedRowKeys.join(",")
            }
            name={name}
          />
        ),
        [tableState, name, selectAllInfo],
      )}
      {tableLegend}
    </Container>
  )
}

// function setLocalStorageValues(tableId, key, value) {
//   try {
//     const storedValue = JSON.parse(localStorage.getItem(tableId)) || {}
//
//     storedValue[key] = value
//
//     localStorage.setItem(tableId, JSON.stringify(storedValue))
//   } catch {
//     // empty
//   }
// }
//
// function getLocalStorageValues(tableId, key) {
//   try {
//     if (!tableId) {
//       return null
//     }
//
//     const storedValue = JSON.parse(localStorage.getItem(tableId) || null)
//
//     if (!storedValue) {
//       return null
//     }
//
//     return storedValue[key]
//   } catch {
//     // empty
//   }
// }

const Container = styled(Paper)({
  display: "flex",
  flexFlow: "column",
  flex: 1
})
